<template>
    <div
        class="ob1-price"
        :class="{'ob1-price-mini': isMini, 'ob1-price-compact': isCompact, 'ob1-price-inverse': isInverse}"
        data-ob1-component="Price"
    >
        <p class="visually-hidden" v-html="priceAccessibility" />
        <p aria-hidden="true">
            <template v-if="priceLabel">
                <span class="ob1-price-amount-label" v-html="priceLabel" />
            </template>
            <template v-else>
                <span v-if="showFrom" class="ob1-price-from">à partir de</span>
                <span :class="priceClassObject" v-html="formattedPrice" />
                <span v-if="showByMonth" class="ob1-price-by-month">/mois</span>
            </template>
            <span
                v-if="showInitialPriceStrikethrough"
                :class="initialPriceStrikethroughClasses"
                v-html="formattedInitialPriceStrikethrough"
            />
            <span v-if="showPromoDuration && !cashbackType" class="ob1-price-promo-duration">
                <span v-html="getDuration" />
                <span
                    class="ob1-price-promo-duration-by-month"
                    v-html="formattedInitialPrice + '/mois'"
                />
            </span>
            <span v-if="showUntilEndOfPromo" class="ob1-price-promo-duration">
                <span>jusqu'à la fin de vos promotions en cours puis </span>
                <span
                    class="ob1-price-promo-duration-by-month"
                    v-html="formattedInitialPrice + '/mois'"
                />
            </span>
            <span v-if="showEndOfPromotionPrice && !cashbackType" class="ob1-price-promo-duration">
                <span>puis </span>
                <span
                    class="ob1-price-promo-duration-by-month"
                    v-html="formattedInitialPrice + '/mois'"
                />
                <span> à la fin de vos promotions</span>
            </span>
            <span v-if="cashbackType" class="ob1-price-promo-duration">
                <span v-if="duration >= 1 && !showEndOfPromotionPrice">
                    <span>Pendant {{ duration }} mois,</span>
                    <span v-if="cashbackMentionWithDuration"> {{ cashbackMentionWithDuration }} </span>
                </span>
                <span v-else>
                    <span v-if="cashbackMention"> {{ cashbackMention }} </span>
                </span>
                <span> (après validation du remboursement)</span>
                <span v-if="duration >= 1">
                    <span>puis </span>
                    <span
                        class="ob1-price-promo-duration-by-month"
                        v-html="formattedInitialPrice + '/mois'"
                    />
                    <span v-if="showEndOfPromotionPrice"> à la fin de vos promotions</span>
                </span>
            </span>
            <span v-if="showChildrenPromotionPrice" class="ob1-price-promo-duration">
                <span>pour vos enfants de -18&nbsp;ans au lieu de </span>
                <span
                    class="ob1-price-promo-duration-by-month"
                    v-html="formattedInitialPrice + '/mois'"
                />
                <span><sup>(1)</sup></span>
            </span>
            <span
                v-if="priceDetails && priceDetails.length > 0"
                class="ob1-price-promo-duration pt-0"
                v-html="priceDetails"
            />
        </p>
    </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";
import {formatPriceInCentsToEuros, formatPriceToEuros} from "../../utils/Number";
import {PriceDetailsCashbackType} from "../custom/models/PriceDetailsPopinDto";

export default defineComponent({
    name: "Price",
    props: {
        price: {
            type: Number,
            required: true
        },
        initialPrice: {
            type: Number,
            default: 0
        },
        initialPriceStrikethrough: {
            type: Number,
            default: 0
        },
        priceLabel: {
            type: String,
            default: undefined
        },
        priceDetails: {
            type: String,
            default: undefined
        },
        cashbackType: {
            type: String as PropType<PriceDetailsCashbackType | undefined>,
            required: false,
            default: undefined,
        },
        showFrom: {
            type: Boolean,
            default: false
        },
        showByMonth: {
            type: Boolean,
            default: false
        },
        showPromoDuration: {
            type: Boolean,
            default: false
        },
        showUntilEndOfPromo: {
            type: Boolean,
            default: false
        },
        showEndOfPromotionPrice: {
            type: Boolean,
            default: false
        },
        showChildrenPromotionPrice: {
            type: Boolean,
            default: false
        },
        duration: {
            type: Number,
            default: 0
        },
        isMini: {
            type: Boolean,
            default: false
        },
        isCompact: {
            type: Boolean,
            default: false
        },
        isInverse: {
            type: Boolean,
            default: false
        },
        isCentsFormat: {
            type: Boolean,
            default: true
        },
        custom: {
            type: String,
            default: undefined
        }
    },
    computed: {
        formattedPrice(): string {
            if (this.isCentsFormat) {
                return formatPriceInCentsToEuros(this.price, false);
            } else {
                return formatPriceToEuros(this.price, false);
            }
        },
        formattedInitialPrice(): string {
            if (this.isCentsFormat) {
                return formatPriceInCentsToEuros(this.initialPrice, false);
            } else {
                return formatPriceToEuros(this.initialPrice, false);
            }
        },
        formattedInitialPriceStrikethrough(): string {
            if (this.isCentsFormat) {
                return formatPriceInCentsToEuros(this.initialPriceStrikethrough, false);
            } else {
                return formatPriceToEuros(this.initialPriceStrikethrough, false);
            }
        },
        priceAccessibility(): string {
            let pricePrefix;
            if (this.priceLabel) {
                pricePrefix = `${this.priceLabel} `;
            } else {
                pricePrefix = (this.showFrom ? 'à partir de ' : ' ') + this.formattedPrice + (this.showByMonth ? ' par mois ' : ' ');
            }
            if (this.showInitialPriceStrikethrough) {
                pricePrefix = pricePrefix + 'au lieu de ' + this.formattedInitialPriceStrikethrough + ' ';
            }
            if (this.cashbackType) {
                let cashbackMention = '';

                if (this.duration >= 1 && !this.showEndOfPromotionPrice) {
                    cashbackMention = 'Pendant ' + this.duration + ' mois'
                        + (this.cashbackMentionWithDuration ? (', ' + this.cashbackMentionWithDuration) : '');
                } else {
                    cashbackMention = this.cashbackMention;
                }

                pricePrefix = pricePrefix + cashbackMention
                    + ' (après validation du remboursement)'
                    + (this.duration >= 1 ? ', puis ' + this.formattedInitialPrice + ' par mois' : '')
                    + ((this.duration >= 1 && this.showEndOfPromotionPrice) ? ' à la fin de vos promotions' : '');
            } else if (this.showPromoDuration) {
                pricePrefix = pricePrefix + 'pendant ' + this.duration + ' mois puis ' + this.formattedInitialPrice + ' par mois';
            } else if (this.showUntilEndOfPromo) {
                pricePrefix = pricePrefix + 'jusqu\'à la fin de vos promotions en cours puis ' + this.formattedInitialPrice + ' par mois';
            } else if (this.showEndOfPromotionPrice) {
                pricePrefix = pricePrefix + 'puis ' + this.formattedInitialPrice + ' par mois à la fin de vos promotions';
            } else if (this.showChildrenPromotionPrice) {
                pricePrefix = pricePrefix + 'pour vos enfants de moins de 18 ans au lieu de ' + this.formattedInitialPrice + ' par mois, renvoie note bas de page 1';
            }

            if (this.priceDetails && this.priceDetails.length > 0) {
                pricePrefix = pricePrefix + ', ' + this.priceDetails;
            }
            return pricePrefix;
        },
        priceClassObject(): string {
            if (this.custom != null) {
                return 'ob1-price-amount-custom ' + this.custom;
            } else {
                return 'ob1-price-amount';
            }
        },
        getDuration() {
            if (this.duration == 0) {
                return "au lieu de";
            } else {
                return "pendant " + this.duration + " mois puis";
            }
        },
        showInitialPriceStrikethrough() {
            return (this.initialPriceStrikethrough !== 0 && this.initialPriceStrikethrough !== this.price);
        },
        initialPriceStrikethroughClasses() {
            // Recommandation OB1: Le prix barré sans promo doit s'afficher à côté du prix avec réduction.
            const isPriceStrikethroughWithoutPromo =
                !this.showPromoDuration
                && !this.showUntilEndOfPromo
                && !this.showEndOfPromotionPrice
                && !this.showChildrenPromotionPrice;

            return {
                'ob1-price-without-promo': !isPriceStrikethroughWithoutPromo,
                'ob1-price-without-promo-beside': isPriceStrikethroughWithoutPromo
            };
        },
        cashbackMention(): string {
            if (this.cashbackType == PriceDetailsCashbackType.CHEAT_CODE) {
                return 'Pour les 18-26 ans';
            } else if (this.cashbackType == PriceDetailsCashbackType.SAFER_PHONE) {
                return 'Pour les -18 ans';
            } else {
                return '';
            }
        },
        cashbackMentionWithDuration(): string {
            if (this.cashbackType == PriceDetailsCashbackType.CHEAT_CODE) {
                return 'pour les 18-26 ans';
            } else if (this.cashbackType == PriceDetailsCashbackType.SAFER_PHONE) {
                return 'pour les -18 ans';
            } else {
                return '';
            }
        },
    }
});
</script>
